import { z } from "zod";
/**
 * Themes v2 (docs/PRD-storefront-themes.md §3.2, §5.4): what each business theme lets the merchant choose — curated
 * colour palettes, font pairings and layout/behaviour options — and its defaults. Shared by the API (a saved theme
 * may only use what its base theme declares), the Admin (the customise panel) and the storefront (rendering).
 * Theme tasks ST-13 … ST-23 fill in each theme's palettes, pairings and options.
 */
/** Every option and every value any theme may offer; a theme declares the subset it supports. */
export const THEME_OPTION_VALUES = {
    headerStyle: ["classic", "centered", "split", "minimal", "search_first"],
    cardStyle: ["standard", "overlay", "minimal", "bordered", "list"],
    buttonStyle: ["solid", "outline", "pill", "sharp"],
    colorScheme: ["light", "dark"],
    density: ["compact", "comfortable", "airy"],
    motion: ["full", "subtle", "off"],
    stickyBuyBar: ["on", "off"],
    quickAdd: ["on", "off"],
    showSecondImage: ["on", "off"],
    bottomNav: ["on", "off"],
    cartOpens: ["drawer", "toast"],
    announcementStyle: ["static", "rotating", "marquee"],
    headerScroll: ["sticky", "hide_on_scroll"],
};
export const THEME_OPTION_KEYS = Object.keys(THEME_OPTION_VALUES);
/** The saved options: any known key with any known value; whether the theme allows it is checked separately. */
export const themeOptionsSchema = z
    .object({
    headerStyle: z.enum(THEME_OPTION_VALUES.headerStyle),
    cardStyle: z.enum(THEME_OPTION_VALUES.cardStyle),
    buttonStyle: z.enum(THEME_OPTION_VALUES.buttonStyle),
    colorScheme: z.enum(THEME_OPTION_VALUES.colorScheme),
    density: z.enum(THEME_OPTION_VALUES.density),
    motion: z.enum(THEME_OPTION_VALUES.motion),
    stickyBuyBar: z.enum(THEME_OPTION_VALUES.stickyBuyBar),
    quickAdd: z.enum(THEME_OPTION_VALUES.quickAdd),
    showSecondImage: z.enum(THEME_OPTION_VALUES.showSecondImage),
    bottomNav: z.enum(THEME_OPTION_VALUES.bottomNav),
    cartOpens: z.enum(THEME_OPTION_VALUES.cartOpens),
    announcementStyle: z.enum(THEME_OPTION_VALUES.announcementStyle),
    headerScroll: z.enum(THEME_OPTION_VALUES.headerScroll),
})
    .partial()
    .strict();
/** The options every theme starts from; each declaration below narrows or reorders them (ST-13 … ST-23). */
const BASE_OPTIONS = {
    headerStyle: ["classic"],
    cardStyle: ["standard"],
    buttonStyle: ["pill"],
    colorScheme: ["light"],
    density: ["comfortable"],
    motion: ["full", "subtle", "off"],
    stickyBuyBar: ["on", "off"],
    quickAdd: ["on", "off"],
    showSecondImage: ["on", "off"],
    bottomNav: ["off", "on"],
    cartOpens: ["drawer", "toast"],
    announcementStyle: ["static", "rotating", "marquee"],
    headerScroll: ["sticky", "hide_on_scroll"],
};
/**
 * ST-13 `minimal` — "Mono" (PRD §4.11): Swiss minimal, hairlines, no shadows, Inter only. Ink on white by default,
 * warm grey and dark as alternatives; square corners unless the merchant picks round buttons; fades only.
 */
const mono = {
    palettes: {
        mono: {
            name: { en: "Mono", bn: "মনো" },
            primary: "#111827",
            onPrimary: "#ffffff",
            accent: "#b45309",
            background: "#ffffff",
            surface: "#f5f5f4",
            text: "#111111",
            muted: "#57534e",
            border: "#e7e5e4",
        },
        warm_grey: {
            name: { en: "Warm grey", bn: "উষ্ণ ধূসর" },
            primary: "#292524",
            onPrimary: "#ffffff",
            accent: "#9a3412",
            background: "#faf8f5",
            surface: "#f1ede6",
            text: "#1c1917",
            muted: "#57534e",
            border: "#e2ddd4",
        },
        dark: {
            name: { en: "Dark", bn: "গাঢ়" },
            primary: "#f5f5f4",
            onPrimary: "#111111",
            accent: "#fbbf24",
            background: "#0c0c0c",
            surface: "#171717",
            text: "#f5f5f4",
            muted: "#a8a29e",
            border: "#2e2e2e",
            scheme: "dark",
        },
    },
    defaultPalette: "mono",
    radii: { none: "0px", small: "2px", medium: "4px", large: "6px" },
    // Inter is both the signature and the classic face: one pairing, nothing extra to download.
    fontPairings: ["signature"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["classic", "centered"],
        cardStyle: ["minimal", "bordered"],
        buttonStyle: ["sharp", "pill"],
        density: ["comfortable", "compact", "airy"],
        motion: ["subtle", "off"],
    },
};
/**
 * ST-14 `fashion_women` — "Aanchal" (PRD §4.1): editorial boutique. Ivory and maroon with antique gold for lines
 * only; Jamdani teal and Rose dust as alternatives. Corners 0–4 px, a split header around a centred logo, airy spacing
 * and slow, elegant motion by default.
 */
const aanchal = {
    palettes: {
        aanchal: {
            name: { en: "Aanchal maroon", bn: "আঁচল মেরুন" },
            primary: "#7a1e3a",
            onPrimary: "#ffffff",
            accent: "#b8893b",
            background: "#fbf7f4",
            surface: "#f4e6e3",
            text: "#1f1a1c",
            muted: "#66585d",
            border: "#eadcd7",
        },
        jamdani_teal: {
            name: { en: "Jamdani teal", bn: "জামদানি টিল" },
            primary: "#0f4c4c",
            onPrimary: "#ffffff",
            accent: "#a8803a",
            background: "#fbf8f2",
            surface: "#e9dcc9",
            text: "#1b1f1f",
            muted: "#555b57",
            border: "#e4d8c4",
        },
        rose_dust: {
            name: { en: "Rose dust", bn: "রোজ ডাস্ট" },
            primary: "#8f4560",
            onPrimary: "#ffffff",
            accent: "#b8893b",
            background: "#fffafa",
            surface: "#f7edef",
            text: "#241a1e",
            muted: "#6a5960",
            border: "#efdfe3",
        },
    },
    defaultPalette: "aanchal",
    radii: { none: "0px", small: "2px", medium: "3px", large: "4px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["split", "centered", "classic"],
        cardStyle: ["standard", "minimal"],
        buttonStyle: ["sharp", "pill"],
        density: ["airy", "comfortable"],
        announcementStyle: ["marquee", "rotating", "static"],
    },
};
/**
 * ST-15 `gadgets` — "Volt" (PRD §4.4): dark by default with electric blue, cyan glow and lime for deal timers;
 * "Crimson gamer" (dark) and "Clean light tech" (light) as alternatives. 12 px corners, precise motion.
 */
const volt = {
    palettes: {
        volt: {
            name: { en: "Volt dark", bn: "ভোল্ট ডার্ক" },
            primary: "#3b82f6",
            onPrimary: "#0b0f14",
            accent: "#a3e635",
            background: "#0b0f14",
            card: "#121821",
            surface: "#1a2230",
            text: "#e6edf5",
            muted: "#8b98a9",
            border: "#243042",
            scheme: "dark",
        },
        crimson: {
            name: { en: "Crimson gamer", bn: "ক্রিমসন গেমার" },
            primary: "#f43f5e",
            onPrimary: "#0d0a0b",
            accent: "#facc15",
            background: "#0d0a0b",
            card: "#17111a",
            surface: "#221823",
            text: "#f3eaee",
            muted: "#a8959e",
            border: "#33252f",
            scheme: "dark",
        },
        clean_light: {
            name: { en: "Clean light tech", bn: "ক্লিন লাইট টেক" },
            primary: "#1d4ed8",
            onPrimary: "#ffffff",
            accent: "#4d7c0f",
            background: "#f6f8fb",
            card: "#ffffff",
            surface: "#eaeff6",
            text: "#0f172a",
            muted: "#4b5563",
            border: "#dbe3ee",
        },
    },
    defaultPalette: "volt",
    radii: { none: "0px", small: "6px", medium: "9px", large: "12px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["search_first", "classic"],
        cardStyle: ["standard", "bordered"],
        buttonStyle: ["solid", "pill"],
        motion: ["full", "subtle", "off"],
        density: ["comfortable", "compact"],
        bottomNav: ["on", "off"],
    },
};
/**
 * "Bazaar" (PRD-storefront-themes §4.5, ST-16): fresh and quick — warm white, leaf green, mustard; Nunito / Baloo Da 2;
 * 16 px corners and chunky buttons; the bottom tab bar is on and an added item shows a toast (the sticky cart bar
 * already shows the total), so a shopper can add many items in a row. The accent is always a fill under dark text.
 */
const bazaar = {
    palettes: {
        bazaar: {
            name: { en: "Bazaar green", bn: "বাজার সবুজ" },
            primary: "#15803d",
            onPrimary: "#ffffff",
            accent: "#eab308",
            background: "#fffdf7",
            card: "#ffffff",
            surface: "#eaf5e4",
            text: "#1c2a1e",
            muted: "#4f5d51",
            border: "#e3e8dc",
        },
        organic_olive: {
            name: { en: "Organic olive", bn: "অর্গানিক অলিভ" },
            primary: "#4d6a1f",
            onPrimary: "#ffffff",
            accent: "#e0b43a",
            background: "#fbfaf3",
            card: "#ffffff",
            surface: "#eef0e1",
            text: "#22261a",
            muted: "#575d4a",
            border: "#e1e4d2",
        },
        honey_amber: {
            name: { en: "Honey amber", bn: "হানি অ্যাম্বার" },
            primary: "#b45309",
            onPrimary: "#ffffff",
            accent: "#fbbf24",
            background: "#fffaf0",
            card: "#ffffff",
            surface: "#fdf0d5",
            text: "#2b1d0e",
            muted: "#6b5843",
            border: "#f1e3c6",
        },
    },
    defaultPalette: "bazaar",
    radii: { none: "0px", small: "8px", medium: "12px", large: "16px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["search_first", "classic"],
        cardStyle: ["standard", "bordered"],
        buttonStyle: ["pill", "solid"],
        density: ["comfortable", "compact"],
        bottomNav: ["on", "off"],
        cartOpens: ["toast", "drawer"],
        // A tall header (delivery line, search, categories): out of the way while scrolling down, back on the way up.
        headerScroll: ["hide_on_scroll", "sticky"],
    },
};
/**
 * "Launch" (PRD-storefront-themes §4.10, ST-17): one product, ad traffic — white, ink, a red call to action, a warm
 * panel and trust green; Plus Jakarta Sans / Hind Siliguri; 12 px corners. No bottom tab bar: the phone gets the sticky
 * order bar instead. Red stays large on the warm panel (4.4:1 there; ≥ 4.8:1 on white).
 */
const launch = {
    palettes: {
        launch: {
            name: { en: "Launch red", bn: "লঞ্চ লাল" },
            primary: "#dc2626",
            onPrimary: "#ffffff",
            accent: "#15803d",
            background: "#ffffff",
            card: "#ffffff",
            surface: "#fff4e5",
            text: "#0f172a",
            muted: "#475569",
            border: "#e2e8f0",
        },
        trust_blue: {
            name: { en: "Trust blue", bn: "ট্রাস্ট ব্লু" },
            primary: "#1d4ed8",
            onPrimary: "#ffffff",
            accent: "#15803d",
            background: "#ffffff",
            card: "#ffffff",
            surface: "#eff6ff",
            text: "#0f172a",
            muted: "#475569",
            border: "#dbe4f0",
        },
        premium_black: {
            name: { en: "Premium black", bn: "প্রিমিয়াম কালো" },
            primary: "#111111",
            onPrimary: "#ffffff",
            accent: "#a16207",
            background: "#ffffff",
            card: "#ffffff",
            surface: "#f5f5f4",
            text: "#0a0a0a",
            muted: "#57534e",
            border: "#e7e5e4",
        },
    },
    defaultPalette: "launch",
    radii: { none: "0px", small: "6px", medium: "9px", large: "12px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["minimal", "classic"],
        buttonStyle: ["solid", "pill"],
        density: ["comfortable", "compact"],
        stickyBuyBar: ["on", "off"],
        bottomNav: ["off", "on"],
    },
};
/**
 * "Lavanya" (PRD-storefront-themes §4.3, ST-18): clean beauty — cream, ink, rose, peach panels and sage; Fraunces /
 * Manrope / Anek Bangla; very round (24 px cards, pill buttons), soft coloured shadows. Rose stays large or iconic on
 * the peach panel (3.8:1 there; ≥ 4.7:1 on cream and white). Sage is a fill under ink text.
 */
const lavanya = {
    palettes: {
        lavanya: {
            name: { en: "Lavanya rose", bn: "লাবণ্য রোজ" },
            primary: "#c2416b",
            onPrimary: "#ffffff",
            accent: "#8fae9b",
            background: "#fff8f6",
            card: "#ffffff",
            surface: "#f9ddd6",
            text: "#2b2024",
            muted: "#6b5960",
            border: "#f0dcd6",
        },
        lavender_clean: {
            name: { en: "Lavender clean", bn: "ল্যাভেন্ডার ক্লিন" },
            primary: "#6d5bd0",
            onPrimary: "#ffffff",
            accent: "#a8c5b5",
            background: "#faf8ff",
            card: "#ffffff",
            surface: "#ece8fb",
            text: "#231f33",
            muted: "#5f5a72",
            border: "#e4e0f5",
        },
        aqua_derm: {
            name: { en: "Aqua derm", bn: "অ্যাকোয়া ডার্ম" },
            primary: "#0e7490",
            onPrimary: "#ffffff",
            accent: "#f4b4a6",
            background: "#f5fbfc",
            card: "#ffffff",
            surface: "#ddf1f4",
            text: "#10292e",
            muted: "#4b6469",
            border: "#d4e8ec",
        },
    },
    defaultPalette: "lavanya",
    radii: { none: "0px", small: "12px", medium: "18px", large: "24px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["centered", "classic"],
        cardStyle: ["standard", "minimal"],
        buttonStyle: ["pill", "solid"],
        density: ["comfortable", "airy"],
    },
};
/**
 * ST-19 `fashion_men` — "Shaan" (PRD §4.2): bold menswear, street to Eid. Off-white and charcoal with navy for action
 * and signal orange for combos and sale; Olive & black and Monochrome (black / white, red sale) as alternatives.
 * Square corners (0–4 px), sharp black-or-navy buttons, compact grids, the app-like tab bar on phones by default and
 * the announcement as a marquee.
 */
const shaan = {
    palettes: {
        shaan: {
            name: { en: "Shaan navy", bn: "শান নেভি" },
            primary: "#1e3a8a",
            onPrimary: "#ffffff",
            accent: "#ea580c",
            background: "#f5f3ef",
            card: "#ffffff",
            surface: "#e7e1d6",
            text: "#111111",
            muted: "#55504a",
            border: "#d9d2c5",
        },
        olive_black: {
            name: { en: "Olive & black", bn: "জলপাই ও কালো" },
            primary: "#3f4a24",
            onPrimary: "#ffffff",
            accent: "#c2410c",
            background: "#f3f2ec",
            card: "#ffffff",
            surface: "#e2e2d3",
            text: "#0f0f0d",
            muted: "#4f4f45",
            border: "#d3d3c3",
        },
        monochrome: {
            name: { en: "Monochrome", bn: "সাদা-কালো" },
            primary: "#000000",
            onPrimary: "#ffffff",
            accent: "#dc2626",
            background: "#ffffff",
            card: "#ffffff",
            surface: "#f0f0f0",
            text: "#000000",
            muted: "#525252",
            border: "#d4d4d4",
        },
    },
    defaultPalette: "shaan",
    radii: { none: "0px", small: "2px", medium: "3px", large: "4px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["search_first", "classic"],
        cardStyle: ["standard", "bordered"],
        buttonStyle: ["sharp", "solid"],
        density: ["compact", "comfortable"],
        bottomNav: ["on", "off"],
        announcementStyle: ["marquee", "static", "rotating"],
    },
};
/**
 * ST-20 `jewellery` — "Noor" (PRD §4.8): a luxury boutique — ivory or black (PRD's "scheme option" is the Noor black
 * palette, which turns the dark scheme on itself, as Volt's does), ink, gold only for lines and sheen (never body text), champagne panels; Rose gold and Emerald as
 * alternatives. Square (0 px) everywhere, thin gold borders, very airy, slow motion.
 */
const noor = {
    palettes: {
        noor_ivory: {
            name: { en: "Noor ivory", bn: "নূর আইভরি" },
            primary: "#111111",
            onPrimary: "#ffffff",
            accent: "#c8a24a",
            background: "#faf8f5",
            card: "#ffffff",
            surface: "#f1eadf",
            text: "#141210",
            muted: "#5c544a",
            border: "#e6dccb",
        },
        noor_black: {
            name: { en: "Noor black", bn: "নূর কালো" },
            primary: "#c8a24a",
            onPrimary: "#0e0d0c",
            accent: "#c8a24a",
            background: "#0e0d0c",
            card: "#171512",
            surface: "#1f1c18",
            text: "#f3eee6",
            muted: "#b3aa9a",
            border: "#2f2b25",
            scheme: "dark",
        },
        rose_gold: {
            name: { en: "Rose gold", bn: "রোজ গোল্ড" },
            primary: "#2a1c1c",
            onPrimary: "#ffffff",
            accent: "#b76e79",
            background: "#fbf6f4",
            card: "#ffffff",
            surface: "#f3e4e0",
            text: "#1f1515",
            muted: "#66524f",
            border: "#ecd9d4",
        },
        emerald: {
            name: { en: "Emerald", bn: "পান্না" },
            primary: "#0f4d3a",
            onPrimary: "#ffffff",
            accent: "#c8a24a",
            background: "#f7f6f1",
            card: "#ffffff",
            surface: "#e6ece4",
            text: "#0f1f19",
            muted: "#4a5a52",
            border: "#d6dfd6",
        },
    },
    defaultPalette: "noor_ivory",
    radii: { none: "0px", small: "0px", medium: "2px", large: "4px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["centered", "minimal"],
        cardStyle: ["standard", "minimal"],
        buttonStyle: ["sharp", "outline"],
        density: ["airy", "comfortable"],
    },
};
/**
 * ST-21 `home_kitchen` — "Nest" (PRD §4.6): a warm Scandinavian-meets-Bangladeshi home — linen page, charcoal ink,
 * terracotta for action, olive as an accent, sand panels; Sage home and Charcoal modern as alternatives. 8 px corners
 * (0–12 px scale), soft long shadows, generous space.
 */
const nest = {
    palettes: {
        nest: {
            name: { en: "Nest terracotta", bn: "নেস্ট টেরাকোটা" },
            primary: "#b4532a",
            onPrimary: "#ffffff",
            accent: "#5b6b3a",
            background: "#f7f4ef",
            card: "#ffffff",
            surface: "#ece5da",
            text: "#262421",
            muted: "#5e574e",
            border: "#e0d8cb",
        },
        sage_home: {
            name: { en: "Sage home", bn: "সেজ হোম" },
            primary: "#4a6a52",
            onPrimary: "#ffffff",
            accent: "#b4532a",
            background: "#f5f6f1",
            card: "#ffffff",
            surface: "#e3e8dc",
            text: "#1f2620",
            muted: "#525c53",
            border: "#d6ddd0",
        },
        charcoal_modern: {
            name: { en: "Charcoal modern", bn: "চারকোল মডার্ন" },
            primary: "#262421",
            onPrimary: "#ffffff",
            accent: "#c08a4a",
            background: "#f4f3f1",
            card: "#ffffff",
            surface: "#e6e3de",
            text: "#1b1a18",
            muted: "#57534d",
            border: "#d9d5ce",
        },
    },
    defaultPalette: "nest",
    radii: { none: "0px", small: "4px", medium: "8px", large: "12px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["classic", "centered"],
        cardStyle: ["standard", "minimal"],
        buttonStyle: ["solid", "pill"],
        density: ["airy", "comfortable"],
    },
};
/**
 * ST-22 `kids_baby` — "Tuntuni" (PRD §4.7): playful and safe — cream page, soft ink, coral for action; sky, mint and
 * sunshine section backgrounds rotate (theme CSS). Pastel lilac and Ocean blue as alternatives. Very round (12–24 px),
 * pill buttons with a "3D" bottom edge, comfortable spacing.
 */
const tuntuni = {
    palettes: {
        tuntuni: {
            name: { en: "Tuntuni coral", bn: "টুনটুনি কোরাল" },
            primary: "#d9363e",
            onPrimary: "#ffffff",
            accent: "#bae6fd",
            background: "#fffbf5",
            card: "#ffffff",
            surface: "#fef3d7",
            text: "#2d2a32",
            muted: "#5b5560",
            border: "#f1e6d6",
        },
        pastel_lilac: {
            name: { en: "Pastel lilac", bn: "প্যাস্টেল লাইলাক" },
            primary: "#7c3aed",
            onPrimary: "#ffffff",
            accent: "#fbcfe8",
            background: "#fdfaff",
            card: "#ffffff",
            surface: "#efe7fd",
            text: "#2a2433",
            muted: "#5d5668",
            border: "#e7defa",
        },
        ocean_blue: {
            name: { en: "Ocean blue", bn: "সমুদ্র নীল" },
            primary: "#0369a1",
            onPrimary: "#ffffff",
            accent: "#fde68a",
            background: "#f6fbff",
            card: "#ffffff",
            surface: "#dff1fb",
            text: "#10222e",
            muted: "#4a5d69",
            border: "#d4e8f4",
        },
    },
    defaultPalette: "tuntuni",
    radii: { none: "0px", small: "12px", medium: "18px", large: "24px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["classic", "centered"],
        cardStyle: ["standard", "minimal"],
        buttonStyle: ["pill", "solid"],
        density: ["comfortable", "airy"],
    },
};
/**
 * ST-23 `home_food` — "Rannaghor" (PRD §4.9): a home kitchen's printed menu card — warm cream, chocolate ink, burnt
 * orange for action, butter panels, pistachio as an accent; Berry bakery and Mint café as alternatives. 14 px corners,
 * dashed menu-card borders; cards are photo cards or menu rows (`list`).
 */
const rannaghor = {
    palettes: {
        rannaghor: {
            name: { en: "Rannaghor", bn: "রান্নাঘর" },
            primary: "#c2410c",
            onPrimary: "#ffffff",
            accent: "#7c9a5a",
            background: "#fff7ed",
            card: "#fffdf9",
            surface: "#fdebd0",
            text: "#3f2a1d",
            muted: "#6b5446",
            border: "#f1dcc2",
        },
        berry_bakery: {
            name: { en: "Berry bakery", bn: "বেরি বেকারি" },
            primary: "#a3216b",
            onPrimary: "#ffffff",
            accent: "#e8a33d",
            background: "#fff6f8",
            card: "#ffffff",
            surface: "#fbe1ea",
            text: "#3b1d2a",
            muted: "#6d4a5a",
            border: "#f3d2de",
        },
        mint_cafe: {
            name: { en: "Mint café", bn: "মিন্ট ক্যাফে" },
            primary: "#0f766e",
            onPrimary: "#ffffff",
            accent: "#d97757",
            background: "#f3faf7",
            card: "#ffffff",
            surface: "#d9efe6",
            text: "#183028",
            muted: "#4d6359",
            border: "#cbe5da",
        },
    },
    defaultPalette: "rannaghor",
    radii: { none: "0px", small: "8px", medium: "12px", large: "14px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["classic", "centered"],
        cardStyle: ["standard", "list"],
        buttonStyle: ["pill", "solid"],
        density: ["comfortable", "airy"],
    },
};
/**
 * DG-08 `books` — "Boighor" (docs/phases/phase-5-accounts-ebooks.md): a busy, trustworthy bookshop — paper white,
 * green-black ink, bottle green for action, marigold for offers, a quiet shelf grey; covers are the colour. Midnight
 * library (dark) and Book fair red as alternatives. 6 px corners, a search-first header, 2:3 covers on shelves.
 */
const boighor = {
    palettes: {
        boighor: {
            name: { en: "Boighor", bn: "বইঘর" },
            primary: "#1f5e46",
            onPrimary: "#ffffff",
            accent: "#e8a33d",
            background: "#fafaf7",
            card: "#ffffff",
            surface: "#eef1ec",
            text: "#1c2421",
            muted: "#56625c",
            border: "#dde3dc",
        },
        midnight_library: {
            name: { en: "Midnight library", bn: "রাতের পাঠাগার" },
            primary: "#1f5e46",
            onPrimary: "#ffffff",
            accent: "#e8a33d",
            background: "#f4f1ea",
            card: "#fffdf8",
            surface: "#e7e1d4",
            text: "#141a2b",
            muted: "#4d5366",
            border: "#d9d2c3",
        },
        book_fair_red: {
            name: { en: "Book fair red", bn: "বইমেলা লাল" },
            primary: "#a4231c",
            onPrimary: "#ffffff",
            accent: "#e8a33d",
            background: "#fff9f4",
            card: "#ffffff",
            surface: "#f8e8df",
            text: "#2a1a17",
            muted: "#6b544e",
            border: "#eed8ce",
        },
    },
    defaultPalette: "boighor",
    radii: { none: "0px", small: "4px", medium: "6px", large: "8px" },
    fontPairings: ["signature", "classic"],
    options: {
        ...BASE_OPTIONS,
        headerStyle: ["search_first", "classic"],
        cardStyle: ["standard", "bordered"],
        buttonStyle: ["solid", "pill"],
        density: ["compact", "comfortable"],
        motion: ["subtle", "off"],
    },
};
export const THEME_DECLARATIONS = {
    fashion_women: aanchal,
    fashion_men: shaan,
    cosmetics: lavanya,
    gadgets: volt,
    grocery: bazaar,
    home_kitchen: nest,
    kids_baby: tuntuni,
    jewellery: noor,
    home_food: rannaghor,
    books: boighor,
    single_product: launch,
    minimal: mono,
};
/** The declaration of a base theme; unknown keys fall back to `minimal` (as the storefront does). */
export function themeDeclaration(baseKey) {
    return baseKey in THEME_DECLARATIONS
        ? THEME_DECLARATIONS[baseKey]
        : THEME_DECLARATIONS.minimal;
}
/** The options in effect: the saved value when the theme supports it, else the theme's default. */
export function effectiveOptions(baseKey, saved) {
    const declared = themeDeclaration(baseKey).options;
    const pick = (key) => {
        const allowed = declared[key];
        const value = saved[key];
        return value !== undefined && allowed.includes(value) ? value : declared[key][0];
    };
    return {
        headerStyle: pick("headerStyle"),
        cardStyle: pick("cardStyle"),
        buttonStyle: pick("buttonStyle"),
        colorScheme: pick("colorScheme"),
        density: pick("density"),
        motion: pick("motion"),
        stickyBuyBar: pick("stickyBuyBar"),
        quickAdd: pick("quickAdd"),
        showSecondImage: pick("showSecondImage"),
        bottomNav: pick("bottomNav"),
        cartOpens: pick("cartOpens"),
        announcementStyle: pick("announcementStyle"),
        headerScroll: pick("headerScroll"),
    };
}
/** What a saved theme chooses that its base theme does not offer (the API rejects these with 400). */
export function themeChoiceProblems(theme) {
    const declaration = themeDeclaration(theme.base);
    const problems = [];
    if (theme.palette !== "custom" && !(theme.palette in declaration.palettes))
        problems.push({ path: "palette", message: "This theme has no such colour set" });
    if (!declaration.fontPairings.includes(theme.fontPairing))
        problems.push({ path: "fontPairing", message: "This theme has no such font pairing" });
    for (const key of THEME_OPTION_KEYS) {
        const value = theme.options[key];
        const allowed = declaration.options[key];
        if (value !== undefined && !allowed.includes(value))
            problems.push({ path: `options.${key}`, message: "This theme does not offer this choice" });
    }
    return problems;
}
//# sourceMappingURL=storefront-theme-kits.js.map